import React, { useEffect, useState, useCallback, useRef } from "react";
import { io as socketIO } from "socket.io-client";
import ApiService from "../../services/apiService";

/* ─── CONSTANTS ──────────────────────────────────────────────── */
const STATUS_CFG = {
  active:    { label: "Active",    color: "#3b82f6", bg: "#eff6ff", icon: "fa-spin fa-spinner" },
  assigned:  { label: "Assigned",  color: "#22c55e", bg: "#f0fdf4", icon: "fa-check-circle"    },
  escalated: { label: "Escalated", color: "#ef4444", bg: "#fef2f2", icon: "fa-exclamation-circle" },
  cancelled: { label: "Cancelled", color: "#9ca3af", bg: "#f9fafb", icon: "fa-times-circle"    },
};

const OFFER_CFG = {
  pending:  { label: "Pending",  color: "#f97316", bg: "#fff7ed" },
  accepted: { label: "Accepted", color: "#22c55e", bg: "#f0fdf4" },
  declined: { label: "Declined", color: "#ef4444", bg: "#fef2f2" },
  expired:  { label: "Expired",  color: "#9ca3af", bg: "#f9fafb" },
};

const RADIUS_COLORS = { 5: "#3b82f6", 10: "#8b5cf6", 15: "#f97316", 20: "#ef4444" };

const TH = {
  padding: "10px 14px", fontSize: "11px", fontWeight: 600, color: "#6b7280",
  textTransform: "uppercase", letterSpacing: "0.4px",
  borderBottom: "1px solid #e9ecef", whiteSpace: "nowrap", background: "#f9fafb",
};
const TD = { padding: "10px 14px", verticalAlign: "middle", borderBottom: "1px solid #f5f5f5" };

/* ─── HELPERS ────────────────────────────────────────────────── */
function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function timeLeft(end) {
  if (!end) return null;
  const ms = new Date(end) - Date.now();
  if (ms <= 0) return "Expired";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m left`;
  return `${m}m ${s}s left`;
}

/* ─── LIVE COUNTDOWN ─────────────────────────────────────────── */
function LiveCountdown({ end }) {
  const calc = () => {
    if (!end) return null;
    const ms = new Date(end) - Date.now();
    if (ms <= 0) return { label: "Expired", urgent: true };
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const urgent = ms < 10 * 60 * 1000;
    if (h > 0) return { label: `${h}h ${m}m ${s}s left`, urgent };
    return { label: `${m}m ${s}s left`, urgent };
  };

  const [tick, setTick] = useState(calc);

  useEffect(() => {
    setTick(calc());
    const id = setInterval(() => setTick(calc()), 1000);
    return () => clearInterval(id);
  }, [end]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tick) return <span style={{ color: "#9ca3af" }}>—</span>;
  return (
    <span style={{ fontSize: "11px", fontWeight: tick.urgent ? 700 : 400, color: tick.urgent ? "#ef4444" : "#374151" }}>
      {tick.label}
    </span>
  );
}

/* ─── STAT CARD ──────────────────────────────────────────────── */
function StatCard({ label, value, color, bg, icon, active, onClick, badge }) {
  return (
    <div onClick={onClick} style={{
      flex: "1 1 130px", background: active ? bg : "#fff",
      border: `1px solid ${active ? color + "60" : "#e9ecef"}`,
      borderRadius: 10, padding: "14px 16px", cursor: onClick ? "pointer" : "default",
      boxShadow: active ? `0 0 0 2px ${color}20` : "none", transition: "all 0.15s",
      position: "relative",
    }}>
      {badge > 0 && (
        <div style={{
          position: "absolute", top: -7, right: -7,
          background: "#ef4444", color: "#fff",
          borderRadius: "50%", minWidth: 20, height: 20,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "10px", fontWeight: 800, padding: "0 4px",
          boxShadow: "0 1px 4px rgba(239,68,68,0.5)",
        }}>{badge}</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className={`fa ${icon}`} style={{ fontSize: 15, color }} />
        </div>
        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: "26px", fontWeight: 700, color: "#111827" }}>{value ?? 0}</div>
    </div>
  );
}

/* ─── OFFER BADGE ────────────────────────────────────────────── */
function OfferBadge({ status }) {
  const c = OFFER_CFG[status] || OFFER_CFG.expired;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 12,
      background: c.bg, color: c.color, fontSize: "10px", fontWeight: 600,
    }}>{c.label}</span>
  );
}

/* ─── RADIUS BADGE ───────────────────────────────────────────── */
function RadiusBadge({ radius }) {
  const color = RADIUS_COLORS[radius] || "#6b7280";
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 12,
      background: color + "15", color, fontSize: "11px", fontWeight: 700,
    }}>{radius}km</span>
  );
}

/* ─── OFFERS EXPAND PANEL ────────────────────────────────────── */
function OffersPanel({ offers }) {
  if (!offers || offers.length === 0) {
    return <span style={{ fontSize: "11px", color: "#9ca3af" }}>No offers sent yet</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {offers.map((o, i) => (
        <div key={i} style={{
          background: "#f9fafb", border: "1px solid #e9ecef", borderRadius: 7,
          padding: "5px 10px", fontSize: "11px",
        }}>
          <span style={{ fontWeight: 600, color: "#374151" }}>{o.panditName || `Pandit #${i + 1}`}</span>
          <span style={{ color: "#9ca3af", marginLeft: 5 }}>Wave {o.wave}</span>
          <span style={{ marginLeft: 8 }}><OfferBadge status={o.status} /></span>
          {o.responseTimeSecs != null && (
            <span style={{ color: "#9ca3af", fontSize: "10px", marginLeft: 6 }}>
              ({o.responseTimeSecs}s)
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
export default function AllocationDashboard() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [seenStats, setSeenStats] = useState({});

  const statusFilterRef = useRef(statusFilter);
  useEffect(() => { statusFilterRef.current = statusFilter; }, [statusFilter]);

  const fetchStats = useCallback(() => {
    ApiService.get("allocation/stats")
      .then(d => {
        if (!d?.success) return;
        setStats(d);
        const newCounts = d.jobs || {};
        // Auto-mark the currently viewed filter as seen so it never badges
        setSeenStats(prev => ({
          ...prev,
          [statusFilterRef.current]: newCounts[statusFilterRef.current] ?? 0,
        }));
      })
      .catch(() => {});
  }, []);

  const fetchJobs = useCallback(() => {
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : "";
    ApiService.get(`allocation/jobs${params}`)
      .then(d => {
        setLoading(false);
        if (d?.success) {
          setJobs(d.data);
          setLastRefresh(new Date());
        }
      })
      .catch(err => { console.error("[fetchJobs]", err); setLoading(false); });
  }, [statusFilter]);

  useEffect(() => {
    fetchStats();
    fetchJobs();

    const socket = socketIO(process.env.REACT_APP_PANDIT_SERVICE_URL || "http://localhost:8001", {
      transports: ["websocket", "polling"],
    });

    socket.on("connect", () => socket.emit("join:ops"));

    const refresh = () => { fetchStats(); fetchJobs(); };
    socket.on("allocation:started",  refresh);
    socket.on("allocation:wave",     refresh);
    socket.on("allocation:expanded", refresh);
    socket.on("allocation:assigned", refresh);
    socket.on("ops:escalation",      refresh);

    return () => socket.disconnect();
  }, [fetchStats, fetchJobs]);

  const jobStats = stats?.jobs || {};

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h4 style={{ fontWeight: 700, color: "#111827", margin: "0 0 2px" }}>Allocation Engine</h4>
          <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>
            Live status of pandit allocation jobs and booking offers
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {lastRefresh && (
            <span style={{ fontSize: "11px", color: "#9ca3af" }}>
              Updated {lastRefresh.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => { fetchStats(); fetchJobs(); }}
            style={{ fontSize: "12px" }}
          >
            <i className="fa fa-refresh" style={{ marginRight: 5 }} />Refresh
          </button>
        </div>
      </div>

      {/* Allocation Job Stats */}
      <div style={{ marginBottom: 6 }}>
        <span style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px" }}>
          Allocation Jobs
        </span>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {Object.entries(STATUS_CFG).map(([key, cfg]) => {
          const badgeable = key === "active" || key === "escalated";
          const badge = badgeable ? Math.max(0, (jobStats[key] || 0) - (seenStats[key] ?? jobStats[key] ?? 0)) : 0;
          return (
            <StatCard
              key={key}
              label={cfg.label}
              value={jobStats[key]}
              color={cfg.color}
              bg={cfg.bg}
              icon={cfg.icon}
              active={statusFilter === key}
              badge={badge}
              onClick={() => {
                const next = statusFilter === key ? "" : key;
                setStatusFilter(next);
                setExpandedJob(null);
                setSeenStats(prev => ({ ...prev, [key]: jobStats[key] ?? 0 }));
              }}
            />
          );
        })}
        <StatCard
          label="Total Jobs"
          value={jobStats.total}
          color="#111827"
          bg="#f9fafb"
          icon="fa-list"
          active={statusFilter === ""}
          onClick={() => { setStatusFilter(""); setExpandedJob(null); }}
        />
      </div>

      {/* Jobs Table */}
      <div style={{ background: "#fff", border: "1px solid #e9ecef", borderRadius: 10, overflow: "hidden" }}>
        <div style={{
          padding: "12px 16px", borderBottom: "1px solid #f0f0f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#111827" }}>
            {statusFilter ? `${STATUS_CFG[statusFilter]?.label || statusFilter} Jobs` : "All Jobs"}
            <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>
              ({jobs.length})
            </span>
          </h5>
          <div style={{ display: "flex", gap: 6 }}>
            {["active", "assigned", "escalated", "cancelled", ""].map(s => (
              <button
                key={s ?? "all"}
                onClick={() => { setStatusFilter(s); setExpandedJob(null); }}
                className={`btn btn-xs ${statusFilter === s ? "btn-primary" : "btn-outline-secondary"}`}
                style={{ fontSize: "11px" }}
              >
                {s ? (STATUS_CFG[s]?.label || s) : "All"}
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="table mb-0" style={{ fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={TH}>Booking</th>
                <th style={TH}>Service</th>
                <th style={TH}>Customer</th>
                <th style={TH}>Status</th>
                <th style={TH}>Radius</th>
                <th style={TH}>Wave</th>
                <th style={TH}>Contacted</th>
                <th style={TH}>Window Ends</th>
                <th style={TH}>Pandit Assigned</th>
                <th style={TH}>Offers</th>
              </tr>
            </thead>
            <tbody>
              {loading && jobs.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-4" style={{ color: "#9ca3af" }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />Loading...
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-4" style={{ color: "#9ca3af" }}>
                    No allocation jobs found
                  </td>
                </tr>
              ) : jobs.map(job => {
                const sCfg = STATUS_CFG[job.status] || STATUS_CFG.cancelled;
                const isExpanded = expandedJob === job.id;

                return (
                  <React.Fragment key={job.id}>
                    <tr style={{ background: isExpanded ? "#f8faff" : undefined }}>
                      <td style={TD}>
                        <div style={{ fontFamily: "monospace", fontSize: "11px", color: "#374151", maxWidth: 140 }}>
                          {job.bookingId?.substring(0, 18)}...
                        </div>
                        <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: 2 }}>
                          {fmt(job.createdAt)}
                        </div>
                      </td>
                      <td style={TD}>
                        <span style={{ fontSize: "12px", color: "#374151" }}>{job.serviceName || "—"}</span>
                        {job.pujaDateTime && (
                          <div style={{ fontSize: "10px", color: "#9ca3af" }}>
                            {new Date(job.pujaDateTime).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                          </div>
                        )}
                      </td>
                      <td style={TD}>
                        <div style={{ fontSize: "12px", fontWeight: 500, color: "#111827" }}>{job.customerName || "—"}</div>
                        <div style={{ fontSize: "10px", color: "#9ca3af" }}>{job.customerPhone || ""}</div>
                      </td>
                      <td style={TD}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          padding: "3px 10px", borderRadius: 12,
                          background: sCfg.bg, color: sCfg.color,
                          fontSize: "11px", fontWeight: 600,
                        }}>
                          <i className={`fa ${sCfg.icon}`} style={{ fontSize: 10 }} />
                          {sCfg.label}
                        </span>
                      </td>
                      <td style={TD}><RadiusBadge radius={job.currentRadius} /></td>
                      <td style={TD}>
                        <span style={{ fontWeight: 600, color: "#374151" }}>#{job.windowNumber}</span>
                      </td>
                      <td style={TD}>
                        <span style={{
                          display: "inline-block", minWidth: 24, textAlign: "center",
                          background: "#f3f4f6", borderRadius: 12,
                          padding: "1px 8px", fontSize: "12px", fontWeight: 700, color: "#374151",
                        }}>
                          {job.panditsContactedCount}
                        </span>
                      </td>
                      <td style={TD}>
                        {job.status === "active" ? (
                          <div>
                            <LiveCountdown end={job.windowEnd} />
                            <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: 2 }}>{fmt(job.windowEnd)}</div>
                          </div>
                        ) : <span style={{ color: "#9ca3af", fontSize: "11px" }}>—</span>}
                      </td>
                      <td style={TD}>
                        {job.panditName ? (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "12px", color: "#22c55e" }}>{job.panditName}</div>
                            <div style={{ fontSize: "10px", color: "#9ca3af" }}>{job.panditPhone}</div>
                          </div>
                        ) : <span style={{ color: "#9ca3af", fontSize: "11px" }}>Not assigned</span>}
                      </td>
                      <td style={TD}>
                        <button
                          className="btn btn-xs btn-outline-secondary"
                          onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                          style={{ fontSize: "11px" }}
                        >
                          <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`} style={{ marginRight: 4 }} />
                          {job.offers?.length || 0} offers
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr style={{ background: "#f8faff" }}>
                        <td colSpan={10} style={{ padding: "10px 16px 14px", borderBottom: "1px solid #e9ecef" }}>
                          <div style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280", marginBottom: 8, textTransform: "uppercase" }}>
                            Offers sent for this booking
                          </div>
                          <OffersPanel offers={job.offers} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {jobs.length > 0 && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid #f0f0f0" }}>
            <span style={{ fontSize: "12px", color: "#6b7280" }}>
              Showing {jobs.length} job{jobs.length !== 1 ? "s" : ""} · Auto-refreshes every 15 seconds
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
